import { call, put, takeLatest } from 'redux-saga/effects';
import * as T from './offersReportConstants';
import * as actions from './offersReportActions';
import {
  getOffersSummaryApi,
  getOfferKpiComparisonApi,
  getTopOffersApi,
  getOfferRedemptionTrendApi,
  getOfferDiscountBreakdownApi,
  getOfferDiscountByTypeApi,
  getOfferDetailApi,
} from './offersReportApi';

function* fetchOffersSummarySaga(
  action: ReturnType<typeof actions.fetchOffersSummaryRequest>
) {
  try {
    const response: any = yield call(getOffersSummaryApi, action.payload.locationId, action.payload.startDate, action.payload.endDate);
    yield put(actions.fetchOffersSummarySuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOffersSummaryFailure(error.message || 'Failed to fetch offers summary'));
  }
}

function* fetchOfferKpiComparisonSaga(
  action: ReturnType<typeof actions.fetchOfferKpiComparisonRequest>
) {
  try {
    const response: any = yield call(getOfferKpiComparisonApi, action.payload.locationId, action.payload.startDate, action.payload.endDate);
    yield put(actions.fetchOfferKpiComparisonSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOfferKpiComparisonFailure(error.message || 'Failed to fetch KPI comparison'));
  }
}

function* fetchTopOffersSaga(
  action: ReturnType<typeof actions.fetchTopOffersRequest>
) {
  try {
    const response: any = yield call(getTopOffersApi, action.payload);
    yield put(actions.fetchTopOffersSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchTopOffersFailure(error.message || 'Failed to fetch top offers'));
  }
}

// Offers KPI row (Active Offers + Top Offer). Same endpoint as the table, but the
// query is fixed: page 1, size 10, redemptions desc, and NO search/status — so the
// tiles show the period's true top offer and active-offer count no matter how the
// table is filtered. The Top Offer tile only ever reads content[0] (see reducer);
// size 10 is just headroom for the reducer to fall back into if content[0] is ever
// unusable. `activeOffers` is a response-level aggregate for the whole range (not a
// tally of `content`), so it's unaffected by the page size.
function* fetchOffersKpiSaga(
  action: ReturnType<typeof actions.fetchOffersKpiRequest>
) {
  try {
    const response: any = yield call(getTopOffersApi, {
      locationId: action.payload.locationId,
      page: 1,
      size: 10,
      sortBy: 'redemptions',
      sortOrder: 'desc',
      startDate: action.payload.startDate,
      endDate: action.payload.endDate,
    });
    yield put(actions.fetchOffersKpiSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOffersKpiFailure(error.message || 'Failed to fetch offers KPIs'));
  }
}

function* fetchOfferRedemptionTrendSaga(
  action: ReturnType<typeof actions.fetchOfferRedemptionTrendRequest>
) {
  try {
    const response: any = yield call(getOfferRedemptionTrendApi, action.payload.locationId, action.payload.startDate, action.payload.endDate);
    yield put(actions.fetchOfferRedemptionTrendSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOfferRedemptionTrendFailure(error.message || 'Failed to fetch redemption trend'));
  }
}

function* fetchOfferDiscountBreakdownSaga(
  action: ReturnType<typeof actions.fetchOfferDiscountBreakdownRequest>
) {
  try {
    const response: any = yield call(getOfferDiscountBreakdownApi, action.payload.locationId, action.payload.startDate, action.payload.endDate);
    yield put(actions.fetchOfferDiscountBreakdownSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOfferDiscountBreakdownFailure(error.message || 'Failed to fetch discount breakdown'));
  }
}

function* fetchOfferDiscountByTypeSaga(
  action: ReturnType<typeof actions.fetchOfferDiscountByTypeRequest>
) {
  try {
    const response: any = yield call(getOfferDiscountByTypeApi, action.payload.locationId, action.payload.startDate, action.payload.endDate);
    yield put(actions.fetchOfferDiscountByTypeSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOfferDiscountByTypeFailure(error.message || 'Failed to fetch discount by type'));
  }
}

function* fetchOfferDetailSaga(
  action: ReturnType<typeof actions.fetchOfferDetailRequest>
) {
  try {
    const response: any = yield call(getOfferDetailApi, action.payload);
    yield put(actions.fetchOfferDetailSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOfferDetailFailure(error.message || 'Failed to fetch offer detail'));
  }
}

export default function* offersReportSaga() {
  yield takeLatest(T.FETCH_OFFERS_SUMMARY_REQUEST, fetchOffersSummarySaga);
  yield takeLatest(T.FETCH_OFFER_KPI_COMPARISON_REQUEST, fetchOfferKpiComparisonSaga);
  yield takeLatest(T.FETCH_TOP_OFFERS_REQUEST, fetchTopOffersSaga);
  yield takeLatest(T.FETCH_OFFERS_KPI_REQUEST, fetchOffersKpiSaga);
  yield takeLatest(T.FETCH_OFFER_REDEMPTION_TREND_REQUEST, fetchOfferRedemptionTrendSaga);
  yield takeLatest(T.FETCH_OFFER_DISCOUNT_BREAKDOWN_REQUEST, fetchOfferDiscountBreakdownSaga);
  yield takeLatest(T.FETCH_OFFER_DISCOUNT_BY_TYPE_REQUEST, fetchOfferDiscountByTypeSaga);
  yield takeLatest(T.FETCH_OFFER_DETAIL_REQUEST, fetchOfferDetailSaga);
}
